import React from 'react';
import {format, parse, isValid} from 'date-fns';
import {Typography, type TypographyProps} from '@mui/material';
import {type CellContext, type RowData} from '@tanstack/react-table';
import {getDateString} from '@netx/core-next/utils/time/getDateString';

export interface DateCellProps<TData extends RowData> extends CellContext<
	TData,
	any
> {
	showTime?: boolean;
	TypographyProps?: Partial<TypographyProps>;
	ref?: React.ForwardedRef<HTMLDivElement>;
}

/**
 * Display a `date` formatted cell\
 */
export const DateCellPrimitive = <TData extends RowData>(
	{showTime, renderValue, TypographyProps}: DateCellProps<TData>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const value = getDateString(renderValue(), showTime);
	return (
		<Typography
			ref={ref}
			title={value}
			variant="body2"
			noWrap
			{...TypographyProps}
		>
			{value}
		</Typography>
	);
};

export const DateCell = React.memo(React.forwardRef(DateCellPrimitive)) as <
	TData extends RowData,
>(
	props: DateCellProps<TData>,
) => JSX.Element;

export default DateCell;
